import React from 'react'
import { Switch, Tag, Tooltip, Typography } from 'antd'
import {
  InfoCircleOutlined,
  QuestionCircleOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { ISyncStatusMeta } from '../utils/syncStatus'
import styles from './SyncStatusPanel.module.scss'

interface ISyncStatusPanelProps {
  meta: ISyncStatusMeta
  isConnected: boolean
  isEnabled: boolean
  isToggling: boolean
  onToggle: (enabled: boolean) => void
}

const LOAD_ERRORS_HINT =
  'Товары не прошли проверку и в HorseSmart не загрузились: не указана категория, цена, описание, фото или габариты. Причина по каждому товару — в отчёте по синхронизации.'
const DISABLED_HINT =
  'Товары групп, у которых в таблице соответствия выключена загрузка. Новые не загружаются, а уже загруженные переведены в статус «Отключен».'

const getTag = (meta: ISyncStatusMeta, isConnected: boolean, isEnabled: boolean) => {
  if (!isConnected) {
    return { color: 'default', label: 'Не подключена' }
  }

  if (!isEnabled) {
    return { color: 'default', label: 'Отключена' }
  }

  if (meta.isProcessing) {
    return { color: 'processing', label: 'В процессе' }
  }

  if (!meta.isVisible) {
    return { color: 'default', label: 'Ещё не запускалась' }
  }

  return { color: 'success', label: 'Завершена' }
}

export const SyncStatusPanel: React.FC<ISyncStatusPanelProps> = ({
  meta,
  isConnected,
  isEnabled,
  isToggling,
  onToggle
}) => {
  const tag = getTag(meta, isConnected, isEnabled)
  const showCounters = isConnected && isEnabled && meta.isVisible

  return (
    <section className={styles.root}>
      <div className={styles.content}>
        <div className={styles.titleRow}>
          <Typography.Text className={styles.title}>
            Синхронизация с МойСклад
          </Typography.Text>
          <Tag
            color={tag.color}
            icon={meta.isProcessing ? <SyncOutlined spin /> : undefined}
          >
            {tag.label}
          </Tag>
        </div>
        <div className={styles.meta}>
          {showCounters ? (
            <>
              <Typography.Text className={styles.metaItem}>
                {meta.isProcessing ? 'Обработано товаров' : 'Всего товаров'}:{' '}
                <span className={styles.metaValue}>{meta.totalCount}</span>
              </Typography.Text>
              <Typography.Text className={styles.metaItem}>
                Ошибок загрузки:{' '}
                <span className={styles.metaValue}>{meta.notPublishedCount}</span>
                <Tooltip title={LOAD_ERRORS_HINT}>
                  <QuestionCircleOutlined className={styles.metaHint} />
                </Tooltip>
              </Typography.Text>
              <Typography.Text className={styles.metaItem}>
                Отключено:{' '}
                <span className={styles.metaValue}>{meta.disabledCount}</span>
                <Tooltip title={DISABLED_HINT}>
                  <InfoCircleOutlined className={styles.metaHint} />
                </Tooltip>
              </Typography.Text>
              {!meta.isProcessing && meta.reportUrl && (
                <Typography.Link
                  href={meta.reportUrl}
                  target='_blank'
                  rel='noreferrer'
                  className={styles.reportLink}
                >
                  Отчет по синхронизации
                </Typography.Link>
              )}
            </>
          ) : (
            <Typography.Text className={styles.metaItem}>
              {isConnected && !isEnabled
                ? 'Остатки обновляются вручную: подтверждайте их в разделе «Товары».'
                : 'Сохраните API-токен и запустите синхронизацию, чтобы увидеть её результат.'}
            </Typography.Text>
          )}
        </div>
      </div>
      <div className={styles.switchBox}>
        <Switch
          checked={isConnected && isEnabled}
          loading={isToggling}
          disabled={!isConnected}
          onChange={onToggle}
        />
        <Typography.Text className={styles.switchLabel}>
          {isConnected && isEnabled
            ? 'Синхронизация включена'
            : 'Синхронизация отключена'}
        </Typography.Text>
      </div>
    </section>
  )
}
